/**
 * LocalStorage utility functions for persisting game data
 * Matches architecture specification in docs/architecture/10-frontend-architecture.md
 */

import type { Settings, Progress } from '../types';

const SETTINGS_KEY = 'wordster_settings';
const PROGRESS_KEY = 'wordster_progress';

/**
 * Load user settings from LocalStorage
 * @returns Settings object or null if not found or invalid
 */
export function loadSettings(): Settings | null {
  try {
    const stored = localStorage.getItem(SETTINGS_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch (error) {
    console.error('Failed to load settings:', error);
    return null;
  }
}

/**
 * Save user settings to LocalStorage
 * @param settings - Settings object to persist
 */
export function saveSettings(settings: Settings): void {
  try {
    localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
  } catch (error) {
    console.error('Failed to save settings:', error);
  }
}

/**
 * Load user progress from LocalStorage
 * @returns Progress object or null if not found or invalid
 */
export function loadProgress(): Progress | null {
  try {
    const stored = localStorage.getItem(PROGRESS_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch (error) {
    console.error('Failed to load progress:', error);
    return null;
  }
}

/**
 * Save user progress to LocalStorage
 * @param progress - Progress object to persist
 */
export function saveProgress(progress: Progress): void {
  try {
    localStorage.setItem(PROGRESS_KEY, JSON.stringify(progress));
  } catch (error) {
    console.error('Failed to save progress:', error);
  }
}

/**
 * Clear all stored data (for testing/reset)
 */
export function clearAllData(): void {
  try {
    localStorage.removeItem(SETTINGS_KEY);
    localStorage.removeItem(PROGRESS_KEY);
  } catch (error) {
    console.error('Failed to clear data:', error);
  }
}
